/**
 * TriggerChips — Action buttons zone of the unified Activity Bar surface.
 *
 * Two display modes:
 *   - **Hero mode** (default, no active chat/flow): Larger centered buttons
 *     with stacked icon above label. Hides when a chat starts.
 *   - **Compact mode** (chat or flow active): Standard single-row chips
 *     (icon + label inline), left-justified directly below the header.
 *
 * Flow-aware swapping:
 *   When `flowChips` is provided (non-empty, exclusive), these replace ALL
 *   buttons entirely. Always renders in compact style.
 *
 * Phase 7: Hero/compact dual mode replaces multi-row layout.
 * See: docs/features/infrastructure/app-shell.md
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { MoreHorizontal, X } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useAppShellContext } from "../../contexts/AppShellContext";
import { menuPaperSx, menuItemSx } from "../../utils/muiOverrides";

const MAX_VISIBLE_CHIPS = 6;

/**
 * Renders a single row of chips with overflow menu.
 */
const ChipRow = ({ chips, onChipClick, disabled, isFlowActive, rowIndex }) => {
    const [overflowAnchor, setOverflowAnchor] = useState(null);

    const visibleChips = isFlowActive ? chips : chips.slice(0, MAX_VISIBLE_CHIPS);
    const overflowChips = isFlowActive ? [] : chips.slice(MAX_VISIBLE_CHIPS);

    const handleChipClick = (chip, event) => {
        if (disabled || chip.disabled) return;
        if (chip.onClick) {
            chip.onClick(event);
            return;
        }
        onChipClick?.(chip);
    };

    if (chips.length === 0) return null;

    return (
        <div className="trigger-chips-row">
            {visibleChips.map((chip, index) => {
                const Icon = chip.icon;
                const isPrimary = chip.primary === true || (!isFlowActive && index === 0 && rowIndex === 0 && chip.primary !== false);
                const isDanger = chip.danger === true;
                const isDangerFilled = chip.dangerFilled === true;
                const isMicChanged = chip.micChanged === true;
                const isAccountAlert = chip.variant === "accountAlert";
                const isDisabled = disabled || chip.disabled;

                let chipClass = "trigger-chip";
                if (isAccountAlert) chipClass += " trigger-chip-account-alert";
                else if (isMicChanged) chipClass += " trigger-chip-mic-changed";
                else if (isDangerFilled) chipClass += " trigger-chip-danger-filled";
                else if (isDanger) chipClass += " trigger-chip-danger";
                else if (isPrimary) chipClass += " trigger-chip-primary";
                else chipClass += " trigger-chip-secondary";

                return (
                    <Tooltip key={chip.id} title={chip.disabledReason || chip.label} arrow>
                        <span>
                            <button
                                className={chipClass}
                                onClick={(e) => handleChipClick(chip, e)}
                                disabled={isDisabled}
                                aria-label={chip.label}
                            >
                                {Icon && <Icon size={14} />}
                                <span className="trigger-chip-label">{chip.label}</span>
                            </button>
                        </span>
                    </Tooltip>
                );
            })}

            {overflowChips.length > 0 && (
                <>
                    <Tooltip title="More actions" arrow>
                        <button
                            className="trigger-chip trigger-chip-overflow"
                            onClick={(e) => setOverflowAnchor(e.currentTarget)}
                            disabled={disabled}
                            aria-label="More actions"
                        >
                            <MoreHorizontal size={14} />
                        </button>
                    </Tooltip>
                    <Menu
                        anchorEl={overflowAnchor}
                        open={Boolean(overflowAnchor)}
                        onClose={() => setOverflowAnchor(null)}
                        slotProps={{
                            paper: { sx: menuPaperSx },
                        }}
                    >
                        {overflowChips.map((chip) => {
                            const Icon = chip.icon;
                            return (
                                <MenuItem
                                    key={chip.id}
                                    onClick={() => {
                                        setOverflowAnchor(null);
                                        handleChipClick(chip);
                                    }}
                                    sx={menuItemSx}
                                >
                                    {Icon && <Icon size={14} />}
                                    {chip.label}
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </>
            )}
        </div>
    );
};

/**
 * @param {Object} props
 * @param {Array}    props.chips - Chip array from useActionChips
 * @param {Object}   props.flowChips - Override chips for active flow: { chips: ChipDef[], exclusive: boolean }
 * @param {Function} props.onChipClick - Handler called with chip object on click
 * @param {boolean}  props.disabled - Disable all chips (e.g. during view-only)
 * @param {boolean}  props.isCompact - When true, render compact style; when false, render hero style
 * @param {Object|null} props.docTypeDropdown - Document type dropdown config: { value, onChange, options: [{ id, name }] }
 * @param {Function|null} props.onCancelConfirmation - Called when user clicks outside the confirmation button
 */
const TriggerChips = ({
    chips = [],
    flowChips = { chips: [], exclusive: false },
    onChipClick,
    disabled = false,
    isCompact = false,
    docTypeDropdown = null,
    onCancelConfirmation = null,
}) => {
    const { selectedPatientName, clearSelectedPatient } = useAppShellContext();
    const chipsContainerRef = useRef(null);

    // Flow chips: exclusive replaces ALL chips, non-exclusive prepends
    const flowChipItems = flowChips.chips || [];
    const isFlowActive = flowChipItems.length > 0;
    const isExclusive = flowChips.exclusive === true;
    const isConfirmationActive = flowChips.confirmationActive === true;

    // Click-outside handler: cancel recording confirmation when user clicks outside the confirmation button
    const handleClickOutside = useCallback((e) => {
        if (!chipsContainerRef.current) return;
        // If the click is inside the chips container, ignore — ChipRow handles it
        if (chipsContainerRef.current.contains(e.target)) return;
        onCancelConfirmation?.();
    }, [onCancelConfirmation]);

    useEffect(() => {
        if (!isConfirmationActive || !onCancelConfirmation) return;
        // Use mousedown so the cancel fires before any other click handler
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isConfirmationActive, onCancelConfirmation, handleClickOutside]);

    // Resolve chip list (flow chips may override)
    const flatChips = chips;

    // When flow is active and exclusive, render as single compact row of flow chips
    if (isFlowActive && isExclusive) {
        const hasDocDropdown = !!docTypeDropdown;
        return (
            <div className={`trigger-chips trigger-chips-horizontal${hasDocDropdown ? " trigger-chips-flow-wrap" : ""}`}>
                {selectedPatientName && (
                    <div className="trigger-chips-selection-indicator">
                        <span>Actions for: <strong>{selectedPatientName}</strong></span>
                        <button
                            className="trigger-chips-selection-clear"
                            onClick={clearSelectedPatient}
                            aria-label="Clear patient selection"
                        >
                            <X size={10} />
                        </button>
                    </div>
                )}
                {hasDocDropdown && (
                    <div className="trigger-chips-doc-type-row">
                        <label className="trigger-chips-doc-type-label">Document type</label>
                        <select
                            className="trigger-chips-doc-type-select"
                            value={docTypeDropdown.value}
                            onChange={docTypeDropdown.onChange}
                        >
                            {docTypeDropdown.options.length === 0 && (
                                <option disabled value="__no_visible_document_types__">No visible document types</option>
                            )}
                            {docTypeDropdown.options.map((opt) => (
                                <option key={opt.id} value={opt.name}>{opt.name}</option>
                            ))}
                            <option disabled value="__manage_separator__">──────────</option>
                            <option value="__manage_document_types__">{docTypeDropdown.manageLabel || "Manage Documents…"}</option>
                        </select>
                    </div>
                )}
                <ChipRow
                    chips={flowChipItems}
                    onChipClick={onChipClick}
                    disabled={disabled}
                    isFlowActive={true}
                    rowIndex={0}
                />
            </div>
        );
    }

    // Merge flow chips (non-exclusive) with normal chips
    // When confirmation is active, replace the "record" chip with the confirmation chip in-place
    let mergedChips = flatChips;
    if (isConfirmationActive && flowChipItems.length > 0) {
        mergedChips = flatChips.map((chip) =>
            chip.id === "record" ? flowChipItems[0] : chip
        );
    }
    const activeChips = (!isConfirmationActive && isFlowActive)
        ? [...flowChipItems, ...mergedChips]
        : mergedChips;

    if (activeChips.length === 0 && !selectedPatientName) return null;

    // Hero mode: centered, stacked icon above label (also used for inline confirmation)
    if (!isCompact && (!isFlowActive || isConfirmationActive)) {
        return (
            <div className="trigger-chips trigger-chips-hero" ref={chipsContainerRef}>
                {selectedPatientName && (
                    <div className="trigger-chips-selection-indicator">
                        <span>Actions for: <strong>{selectedPatientName}</strong></span>
                        <button
                            className="trigger-chips-selection-clear"
                            onClick={clearSelectedPatient}
                            aria-label="Clear patient selection"
                        >
                            <X size={10} />
                        </button>
                    </div>
                )}
                <div className="trigger-chips-hero-row">
                    {activeChips.slice(0, MAX_VISIBLE_CHIPS).map((chip, index) => {
                        const Icon = chip.icon;
                        const isPrimary = chip.primary === true || (index === 0 && chip.primary !== false);
                        const isDanger = chip.danger === true;
                        const isDangerFilled = chip.dangerFilled === true;
                        const isDisabled = disabled || chip.disabled;

                        let chipClass = "trigger-chip trigger-chip-hero-btn";
                        if (isDangerFilled) chipClass += " trigger-chip-danger-filled";
                        else if (isDanger) chipClass += " trigger-chip-danger";
                        else if (isPrimary) chipClass += " trigger-chip-primary";
                        else chipClass += " trigger-chip-secondary";

                        return (
                            <Tooltip key={chip.id} title={chip.disabledReason || chip.label} arrow>
                                <span>
                                    <button
                                        className={chipClass}
                                        onClick={(e) => handleChipClick(chip, e)}
                                        disabled={isDisabled}
                                        aria-label={chip.label}
                                    >
                                        {Icon && <Icon size={20} />}
                                        <span className="trigger-chip-label">{chip.label}</span>
                                    </button>
                                </span>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Compact mode: single row, left-justified
    return (
        <div className="trigger-chips trigger-chips-horizontal" ref={chipsContainerRef}>
            {selectedPatientName && (
                <div className="trigger-chips-selection-indicator">
                    <span>Actions for: <strong>{selectedPatientName}</strong></span>
                    <button
                        className="trigger-chips-selection-clear"
                        onClick={clearSelectedPatient}
                        aria-label="Clear patient selection"
                    >
                        <X size={10} />
                    </button>
                </div>
            )}
            <ChipRow
                chips={activeChips}
                onChipClick={onChipClick}
                disabled={disabled}
                isFlowActive={isFlowActive}
                rowIndex={0}
            />
        </div>
    );

    function handleChipClick(chip, event) {
        if (disabled || chip.disabled) return;
        if (chip.onClick) {
            chip.onClick(event);
            return;
        }
        onChipClick?.(chip);
    }
};

export default TriggerChips;
